import React, { useMemo, useState } from 'react';
import {
  Drawer,
  Collapse,
  Input,
  List,
  Button,
  Typography,
} from 'antd';
import DimensionLabel, { DimensionDescription, getDimensionDescription, getDimensionLabel } from 'components/DimensionLabel';
import useFormatMessage from 'hooks/useFormatMessage';
import useUser from 'hooks/useUser';
import standardDimensions from 'shared/constants/dimensions';

const DIMENSION_CATEGORY_LABELS = {
  geography: 'Geography',
  device: 'Device',
  browser: 'Browser',
  referrer: 'Referrer',
  campaign: 'Campaign',
  visit: 'Visit',
  page: 'Page',
  event: 'Event',
  siteSearch: 'Site search',
  content: 'Content',
  survey: 'Survey',
  goal: 'Goals',
  other: 'Other',
};

const getDimensionCategory = (dimension) => {
  if (['country', 'city', 'region', 'continent', 'subContinent', 'metro'].includes(dimension)) return 'geography';
  if (dimension.startsWith('device')) return 'device';
  if (dimension.startsWith('browser')) return 'browser';
  if (dimension.startsWith('referrer')) return 'referrer';
  if (dimension.startsWith('campaign')) return 'campaign';
  if (dimension.startsWith('visit') || dimension === 'visitorLocalHour') return 'visit';
  if (dimension.startsWith('page')) return 'page';
  if (dimension.startsWith('event')) return 'event';
  if (dimension.startsWith('siteSearch')) return 'siteSearch';
  if (dimension.startsWith('content') || dimension.startsWith('outlink') || dimension.startsWith('download')) return 'content';
  if (dimension.startsWith('survey') || dimension.startsWith('question')) return 'survey';
  if (dimension === 'goal') return 'goal';
  return 'other';
};

const DimensionPickerDrawer = ({
  open,
  onClose,
  onSelectDimension,
}) => {
  const $t = useFormatMessage();
  const { isSuperUser } = useUser();
  const [dimensionSearch, setDimensionSearch] = useState('');
  const [activeDimensionCategories, setActiveDimensionCategories] = useState([]);

  const groupedDimensionOptions = useMemo(() => {
    const groupedDimensions = Object.entries(standardDimensions).reduce((acc, [dimension]) => {
      const category = getDimensionCategory(dimension);
      if (!acc[category]) {
        acc[category] = [];
      }

      acc[category].push({
        value: dimension,
        label: getDimensionLabel({ $t, dimension }),
        description: getDimensionDescription({ $t, dimension }) || '',
      });

      return acc;
    }, {});

    return Object.entries(groupedDimensions)
      .sort(([a], [b]) => {
        const aIndex = Object.keys(DIMENSION_CATEGORY_LABELS).indexOf(a);
        const bIndex = Object.keys(DIMENSION_CATEGORY_LABELS).indexOf(b);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;
        return a.localeCompare(b);
      })
      .map(([category, options]) => ({
        category,
        label: DIMENSION_CATEGORY_LABELS[category] || category,
        options,
      }));
  }, [$t]);

  const filteredDimensionGroups = useMemo(() => {
    const searchValue = dimensionSearch.trim().toLowerCase();

    if (!searchValue) {
      return groupedDimensionOptions;
    }

    return groupedDimensionOptions
      .map(group => ({
        ...group,
        options: group.options.filter(option => (
          option.label.toLowerCase().includes(searchValue)
          || option.description.toLowerCase().includes(searchValue)
        )),
      }))
      .filter(group => group.options.length > 0);
  }, [groupedDimensionOptions, dimensionSearch]);

  const dimensionSections = useMemo(() => {
    const sections = filteredDimensionGroups.map(group => ({
      key: `category:${group.category}`,
      label: group.label,
      options: group.options,
    }));

    return sections;
  }, [filteredDimensionGroups, isSuperUser, dimensionSearch]);

  const collapseActiveKeys = dimensionSearch.trim()
    ? dimensionSections.map(section => section.key)
    : activeDimensionCategories;

  const closeDrawer = () => {
    setDimensionSearch('');
    setActiveDimensionCategories([]);
    onClose();
  };

  return (
    <Drawer
      title="Add dimension"
      open={open}
      onClose={closeDrawer}
      width={640}
    >
      <Input
        value={dimensionSearch}
        onChange={event => setDimensionSearch(event.target.value)}
        placeholder="Search dimensions"
        style={{ marginBottom: 16 }}
      />
      {dimensionSections.length > 0 ? (
        <Collapse
          activeKey={collapseActiveKeys}
          onChange={keys => setActiveDimensionCategories(Array.isArray(keys) ? keys : [keys])}
          items={dimensionSections.map(section => ({
            key: section.key,
            label: section.label,
            children: (
              <List
                split
                dataSource={section.options}
                renderItem={(option) => {
                  const title = <DimensionLabel dimension={option.value} lineBreaks={false} />;
                  const description = <DimensionDescription dimension={option.value} />;

                  return (
                    <List.Item
                      onClick={() => onSelectDimension(option.value)}
                      style={{ cursor: 'pointer' }}
                      extra={(
                        <Button
                          onClick={(event) => {
                            event.stopPropagation();
                            onSelectDimension(option.value);
                          }}
                        >
                          Select
                        </Button>
                      )}
                    >
                      <List.Item.Meta
                        title={title}
                        description={description}
                      />
                    </List.Item>
                  );
                }}
              />
            ),
          }))}
        />
      ) : (
        <Typography.Text type="secondary">
          No dimensions found for this search
        </Typography.Text>
      )}
    </Drawer>
  );
};

export default DimensionPickerDrawer;
